<template>
  <div class="progress-container">
    <!-- 前景文字 (进度条覆盖的部分) -->
    <div
      class="progress-foreground"
      :aria-hidden="true"
    >
      {{ displayText }}
    </div>

    <!-- 背景文字 (未覆盖的部分) -->
    <div
      class="progress-background"
      :aria-hidden="true"
    >
      {{ displayText }}
    </div>
  </div>
</template>

<script lang="ts">
import { COLOR_PRESETS } from '@/styles/colorsPreset'

export const allProgressColorPresets = COLOR_PRESETS
export type ProgressColorPreset = (typeof allProgressColorPresets)[number]

export const allProgressBackgrounds = ['color', 'neutral'] as const
export type ProgressBackground = (typeof allProgressBackgrounds)[number]

export interface ProgressBarProps {
  /** 当前进度值 (0-100) */
  value: number
  /** 最大值，默认为100 */
  max?: number
  /** 颜色，支持预设值 'blue' | 'orange' | 'green' 或任意合法css背景值，默认为 green */
  color?: ProgressColorPreset | string
  background?: ProgressBackground
  /** 显示的文字，支持插值 {value}, {max} 或 {percentage}%，默认为 `{value}/{max}` */
  text?: string
  /** 宽度，可传入任意合法css尺寸值，传入数字视为单位 px，默认为 100% */
  width?: string | number
  /** 高度，可传入任意合法css尺寸值，传入数字视为单位 px，默认为 32px */
  height?: string | number
  /** 是否显示文字 */
  showText?: boolean
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<ProgressBarProps>(), {
  value: 0,
  max: 100,
  color: 'green',
  background: 'color',
  text: '{value}/{max}',
  width: '100%',
  height: '32px',
  showText: true,
})

// 计算进度百分比
const percentage = computed(() => {
  const percent = Math.min(Math.max((props.value / props.max) * 100, 0), 100)
  return Math.round(percent)
})

// 计算显示文字
const displayText = computed(() => {
  if (!props.showText) return ''

  return props.text
    .replace('{value}', props.value.toString())
    .replace('{max}', props.max.toString())
    .replace('{percentage}', percentage.value.toString())
})

const width = computed(() => {
  return typeof props.width === 'number' ? props.width + 'px' : props.width
})

const height = computed(() => {
  return typeof props.height === 'number' ? props.height + 'px' : props.height
})

const fontSize = computed(() => {
  return typeof props.height === 'number'
    ? props.height - 1 + 'px'
    : `calc(${props.height} - 1px)`
})

// 计算前景色（进度条颜色），支持预设值
const foregroundColor = computed(() => {
  const color = props.color
  if (COLOR_PRESETS.includes(color as ProgressColorPreset)) {
    return `linear-gradient(180deg, var(--ld2-tint-${color}-bg-gradient-start) 0%, var(--ld2-tint-${color}-bg-gradient-end) 100%), var(--ld2-tint-${color}-background)`
  }
  return color
})

// 计算背景色，支持预设值
const backgroundColor = computed(() => {
  if (props.background === 'neutral') {
    return `var(--ld2-background-tertiary)`
  }
  const color = props.color
  if (COLOR_PRESETS.includes(color as ProgressColorPreset)) {
    return `color-mix(in srgb, var(--ld2-tint-${color}-background) 20%, transparent)`
  }
  return color
})
</script>

<style scoped>
.progress-container {
  position: relative;
  width: v-bind('width');
  height: v-bind('height');
  overflow: hidden;
}

.progress-foreground,
.progress-background {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  overflow: hidden;

  text-align: center;
  font-family: Rubik;
  font-weight: 600;
  font-size: v-bind('fontSize');
  line-height: 0;
  white-space: nowrap;

  transition: clip-path 0.3s ease;
}

.progress-foreground {
  background: v-bind('foregroundColor');
  color: var(--ld2-label-inverted-primary);
  clip-path: v-bind('`inset(0 ${100 - percentage}% 0 0)`');
}

.progress-background {
  background: v-bind('backgroundColor');
  color: var(--ld2-label-secondary);
  clip-path: v-bind('`inset(0 0 0 ${percentage}%)`');
}
</style>
